import { BoxProps, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { MarkdownAnchorEl } from "./components/MarkdownAnchorEl";
import { MarkdownHeadingEl } from "./components/MarkdownHeadingEl";
import { MarkdownImgEl } from "./components/MarkdownImgEl";
import { MarkdownParagraphEl, MarkdownParagraphElProps } from "./components/MarkdownParagraphEl";

// react-markdown uses remark under the hood for markdown parsing.
// https://github.com/remarkjs/react-markdown
//
// remark plugins:
// All: https://github.com/remarkjs/remark/blob/main/doc/plugins.md#list-of-plugins
// Support GFM: https://github.com/remarkjs/remark-gfm
//
// rehype plugins:
// All: https://github.com/rehypejs/rehype/blob/main/doc/plugins.md#list-of-plugins

const defaultConfig = {
  gfm: true,
  isSkipHtml: true,
  isEmbedYouTubeVideo: true,
};

export interface MarkdownElCommonProps {
  isUseDefaultMargins?: boolean;
  sx?: BoxProps["sx"];
}

export interface MarkdownRendererProps {
  markdown: string | null;
  config?: Partial<typeof defaultConfig>;
  isUseDefaultMargins?: boolean;
  componentProps?: {
    contentWrapper?: Partial<{
      sx?: BoxProps["sx"];
    }>;
    p?: Partial<MarkdownParagraphElProps>;
  };
  fetchMarkDown?: () => Promise<string | null>;
}

export function MarkdownRenderer({
  markdown,
  config,
  isUseDefaultMargins,
  componentProps,
  fetchMarkDown,
}: MarkdownRendererProps) {
  const [markdownFetched, setMarkdownFetched] = useState<string | null>(null);

  const markdownComputed = markdownFetched || markdown;

  const elCommonProps = useMemo<MarkdownElCommonProps>(
    () => ({ isUseDefaultMargins: isUseDefaultMargins }),
    [isUseDefaultMargins],
  );

  const configComputed = useMemo(
    () => ({
      ...defaultConfig,
      ...config,
    }),
    [config],
  );
  const remarkPlugins = useMemo(
    () => [...(configComputed.gfm ? [remarkGfm] : [])],
    [configComputed],
  );

  useEffect(() => {
    if (fetchMarkDown) {
      fetchMarkDown().then((fetched) => {
        setMarkdownFetched(fetched);
      });
    }
  }, [fetchMarkDown]);

  if (!markdownComputed) {
    return null;
  }

  return (
    <Typography
      component='div'
      variant='body1'
      sx={{
        "& > *": {
          marginBlockStart: 0,
          marginBlockEnd: 0,
        },
        ...componentProps?.contentWrapper?.sx,
      }}
    >
      <Markdown
        skipHtml={configComputed.isSkipHtml}
        remarkPlugins={remarkPlugins}
        // HTML equivalents for the things in MD
        components={{
          h1({ children, ...otherProps }) {
            return (
              <MarkdownHeadingEl variant='h1' {...otherProps} {...elCommonProps}>
                {children}
              </MarkdownHeadingEl>
            );
          },
          h2({ children, ...otherProps }) {
            return (
              <MarkdownHeadingEl variant='h2' {...otherProps} {...elCommonProps}>
                {children}
              </MarkdownHeadingEl>
            );
          },
          h3({ children, ...otherProps }) {
            return (
              <MarkdownHeadingEl variant='h3' {...otherProps} {...elCommonProps}>
                {children}
              </MarkdownHeadingEl>
            );
          },
          h4({ children, ...otherProps }) {
            return (
              <MarkdownHeadingEl variant='h4' {...otherProps} {...elCommonProps}>
                {children}
              </MarkdownHeadingEl>
            );
          },
          h5({ children, ...otherProps }) {
            return (
              <MarkdownHeadingEl variant='h5' {...otherProps} {...elCommonProps}>
                {children}
              </MarkdownHeadingEl>
            );
          },
          h6({ children, ...otherProps }) {
            return (
              <MarkdownHeadingEl variant='h6' {...otherProps} {...elCommonProps}>
                {children}
              </MarkdownHeadingEl>
            );
          },
          p({ children, ...otherProps }) {
            return (
              <MarkdownParagraphEl {...otherProps} {...elCommonProps} {...componentProps?.p}>
                {children}
              </MarkdownParagraphEl>
            );
          },
          a(props) {
            return (
              <MarkdownAnchorEl
                isEmbedYouTubeVideo={configComputed.isEmbedYouTubeVideo}
                {...props}
                {...elCommonProps}
              />
            );
          },
          img(props) {
            return <MarkdownImgEl {...props} {...elCommonProps} />;
          },
        }}
      >
        {markdownComputed}
      </Markdown>
    </Typography>
  );
}
